import React from 'react';
import { Box, Typography, IconButton, Avatar } from '@mui/material';
import { Email, LinkedIn, GitHub, LocationOn, Phone } from '@mui/icons-material';
import myPhoto from '../assets/my-photo.jpeg';

// Phone number for local PDF exports only. Set REACT_APP_PHONE in .env.local (git-ignored);
// `yarn deploy` builds with it blanked so it never reaches the public bundle.
const PRINT_PHONE = process.env.REACT_APP_PHONE?.trim();

const printContactRows = [
  [
    { icon: LocationOn, text: 'Nuremberg, Germany' },
    { icon: Email, text: 'ghorbani59@gmail.com' },
    ...(PRINT_PHONE ? [{ icon: Phone, text: PRINT_PHONE }] : []),
  ],
  [
    { icon: LinkedIn, text: 'linkedin.com/in/aghorbani' },
    { icon: GitHub, text: 'github.com/a-ghorbani' },
  ],
];

export const Profile: React.FC = () => {
  const NUREMBERG_MAPS_URL = "https://www.google.com/maps/place/Nuremberg,+Germany";
  const iconButtonSx = {
    color: '#B0BEC5',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      color: '#00D4FF',
      background: 'rgba(0, 212, 255, 0.1)',
      transform: 'scale(1.05)',
    }
  };

  return (
    <Box sx={{
      background: 'rgba(11, 20, 38, 0.8)',
      borderRadius: '12px',
      padding: { xs: '16px', sm: '32px' },
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        background: 'rgba(11, 20, 38, 0.9)',
      },
      '@media print': {
        background: 'white !important',
        border: 'none !important',
        padding: '0 !important',
        transform: 'none !important',
        marginBottom: '12pt !important',
        '&:hover': {
          transform: 'none !important',
          background: 'white !important',
        }
      }
    }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          '@media print': {
            gap: '0'
          }
        }}>
          {/* Simplified terminal header */}
          <Typography className="no-print" sx={{
            color: '#00FF88',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '12px',
            mb: 1
          }}>
            $ whoami
          </Typography>
          <Typography className="no-print" sx={{
            color: '#B0BEC5',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '12px',
            mb: 3
          }}>
            asghar_ghorbani
          </Typography>

          <Avatar
            className="no-print"
            src={myPhoto}
            sx={{
              width: 160,
              height: 160,
              mb: 3,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'scale(1.02)',
              },
              '@media print': {
                width: '64px !important',
                height: '64px !important',
                margin: '0 !important',
                flexShrink: 0,
                transform: 'none !important',
                '&:hover': {
                  transform: 'none !important',
                }
              }
            }}
          />
          <Box sx={{ mb: 3, '@media print': { mb: '2pt !important' } }}>
            <Typography variant="h2" sx={{
              mb: 1,
              fontFamily: '"Inter", sans-serif',
              '@media print': {
                color: '#000000 !important',
                fontSize: '18pt !important',
                fontWeight: 700,
                margin: '0 0 4pt 0 !important'
              }
            }}>
              Asghar Ghorbani, Ph.D.
            </Typography>
            <Typography variant="h5" className="no-print" sx={{
              color: '#00D4FF',
              fontWeight: 500,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '16px',
              '@media print': {
                color: '#333333 !important',
                fontSize: '11pt !important',
                fontWeight: 500,
                margin: '0 0 8pt 0 !important'
              }
            }}>
              &gt; Applied_AI_Engineer.deploy()
            </Typography>
          </Box>
          {/* Simplified contact links */}
          <Box sx={{
            display: 'flex',
            gap: 1.5,
            mb: 4,
            flexWrap: 'wrap',
            '@media print': {
              display: 'none !important'
            }
          }}>
            <IconButton href={NUREMBERG_MAPS_URL} target="_blank" sx={iconButtonSx}>
              <LocationOn sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton href="mailto:ghorbani59@gmail.com" sx={iconButtonSx}>
              <Email sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton href="https://linkedin.com/in/aghorbani" target="_blank" sx={iconButtonSx}>
              <LinkedIn sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton href="https://github.com/a-ghorbani" target="_blank" sx={iconButtonSx}>
              <GitHub sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Print-only contact block */}
          <Box
            className="print-contact"
            sx={{
              display: 'none',
              '@media print': {
                display: 'flex !important',
                flexDirection: 'column',
                gap: '3pt',
                marginBottom: '10pt !important',
              }
            }}
          >
            {printContactRows.map((row, i) => (
              <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', columnGap: '14pt', rowGap: '3pt' }}>
                {row.map(({ icon: Icon, text }) => (
                  <Box key={text} sx={{ display: 'inline-flex', alignItems: 'center', gap: '4pt' }}>
                    <Icon sx={{ fontSize: '10pt', color: '#777777 !important' }} />
                    <Typography component="span" sx={{ color: '#444444 !important', fontSize: '9.5pt !important' }}>
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          <Box sx={{
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            p: 2,
            '@media print': {
              background: 'white !important',
              border: 'none !important',
              padding: '0 !important',
              margin: '0 !important'
            }
          }}>
            <Typography variant="h6" gutterBottom className="no-print" sx={{
              mb: 2,
              color: '#00D4FF',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '14px'
            }}>
              {/* About */}
            </Typography>
            <Typography variant="body1" sx={{
              lineHeight: 1.6,
              fontFamily: '"Inter", sans-serif',
              fontSize: '15px',
              color: '#B0BEC5',
              '@media print': {
                color: '#333333 !important',
                fontSize: '10pt !important',
                lineHeight: 1.4,
                margin: '0 !important'
              }
            }}>
            I build LLM systems and ship them, for enterprise customers and for my own users.
            10+ years in ML. At H2O.ai and DataRobot I took banks and manufacturers from discovery to production with RAG, agents and evaluation frameworks.
            As a founder I build, ship and support my own products. PocketPal AI, an open-source on-device LLM app, has 8k+ GitHub stars.
            Most recently at Meta on on-device LLM inference.
            </Typography>
          </Box>
        </Box>
    </Box>
  );
};
